import type { ReactNode } from 'react';
import { MotionFadeUp } from './motion';

/**
 * The one way a screen says "there is nothing here" — centred, in words,
 * with the next step attached (Google-style empty state: illustration,
 * short headline, one supporting line, one primary action).
 *
 * `icon` and `illustration` are alternatives: `icon` for compact inline
 * placeholders, `illustration` (an image URL, e.g. an imported SVG asset)
 * for full-surface empty states. Both are purely decorative — the words
 * carry the meaning — so they are hidden from assistive technology and the
 * illustration ships an empty alt.
 */
interface EmptyStateProps {
  icon?: ReactNode;
  /** URL of a decorative illustration, e.g. `import illo from '@/assets/images/….svg'`. */
  illustration?: string;
  title: string;
  description?: string;
  /** Primary call-to-action, e.g. a `<Button>`. */
  action?: ReactNode;
}

export function EmptyState({ icon, illustration, title, description, action }: EmptyStateProps) {
  return (
    <MotionFadeUp className="ui-emptyState" role="status" delay={0.05}>
      {illustration && (
        <img
          className="ui-emptyStateIllustration"
          src={illustration}
          alt=""
          aria-hidden="true"
          loading="lazy"
        />
      )}
      {!illustration && icon && <div className="ui-emptyStateIcon" aria-hidden="true">{icon}</div>}
      <h2>{title}</h2>
      {description && <p>{description}</p>}
      {action && <div className="ui-emptyStateAction">{action}</div>}
    </MotionFadeUp>
  );
}
